import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { retryTask } from '@/api/tasks';
import StatusBadge from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { FAILURE_STATUSES, type TaskListItem } from '@/types/api';
import { formatDateTime } from '@/lib/format';

export default function TasksTable({
  items,
  loading,
}: {
  items: TaskListItem[];
  loading: boolean;
}) {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const retryMutation = useMutation({
    mutationFn: retryTask,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['statuses'] });
    },
  });

  const columns: ColumnDef<TaskListItem>[] = [
    { accessorKey: 'id', header: t('table.id') },
    {
      accessorKey: 'call_started_at',
      header: t('table.started'),
      cell: ({ getValue }) => formatDateTime(getValue() as string | null),
    },
    { accessorKey: 'num_a', header: t('table.numA') },
    { accessorKey: 'num_b', header: t('table.numB') },
    {
      accessorKey: 'status',
      header: t('table.status'),
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const isFailed = (FAILURE_STATUSES as readonly string[]).includes(row.original.status);
        if (!isFailed) return null;
        return (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            disabled={retryMutation.isPending && retryMutation.variables === row.original.id}
            onClick={(e) => {
              e.stopPropagation();
              retryMutation.mutate(row.original.id);
            }}
          >
            {t('actions.retry')}
          </Button>
        );
      },
    },
  ];

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading) {
    return <div className="text-sm text-muted-foreground">{t('table.loading')}</div>;
  }
  if (items.length === 0) {
    return <div className="text-sm text-muted-foreground">{t('table.empty')}</div>;
  }

  return (
    <div className="overflow-auto rounded-md border">
      <table className="w-full text-xs">
        <thead className="bg-muted/50">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th key={h.id} className="px-2 py-1 text-left font-medium">
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => navigate(`/tasks/${row.original.id}`)}
              className="cursor-pointer border-t hover:bg-accent"
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-2 py-1">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
